import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import { useHandleShowDialogs } from 'forms/hooks';
import { includes } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import useFormFieldValue from '@gef-ui/form-core/hooks/state/useFormFieldValue';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import { getLabelMessage } from '@gef-ui/localization/messages';
import ModalLoader from '@gef-ui/material/components/ModalLoader';

import { ComponentLoader } from 'components/index';
import { urls } from 'features/common/fetch/constants';
import { tryCreatingTask } from 'features/views/taskCreatingPages/actions';
import {
	taskTypeBackoffice,
	taskTypeContactClient,
	taskTypeMobileScan,
	taskTypeSellerRequest,
} from 'features/views/taskCreatingPages/constants';
import { CONTACT_CLIENT_FORM_NAME, FIELDS } from 'features/views/taskCreatingPages/form';
import { getTaskIsBeingCreated } from 'features/views/taskCreatingPages/selectors';

export const ActionButtons = ({ formName }) => {
	const dispatch = useDispatch();
	const isTaskBeingCreated = useSelector(getTaskIsBeingCreated);
	const taskType = useFormFieldValue({ fieldName: FIELDS.taskType });
	const isTask = includes(taskType, [
		taskTypeBackoffice,
		taskTypeMobileScan,
		taskTypeSellerRequest,
		taskTypeContactClient,
	]);

	const handleSubmit = useCallback(() => {
		dispatch(tryCreatingTask(formName));
	}, [dispatch, formName]);

	// all pages except contactClient type have "email or phone number" required logic
	// contactClient has exception - only phoneNumber is required - so we dont use "emailOrPhoneNumber" substitute in mapper
	const handleShowDialogsForAddSubmit = useHandleShowDialogs({
		page: formName,
		mapper: {
			name: ['firstName', 'surname', 'businessName'],
			loginOrSellerInOrUserSurname: ['login', 'sellerIn', 'userSurname'],
			...(formName !== CONTACT_CLIENT_FORM_NAME && {
				emailOrPhoneNumber: ['email', 'phoneNumber'],
			}),
		},
		continueFn: handleSubmit,
	});

	return (
		<Grid container>
			<ModalLoader
				open={isTaskBeingCreated}
				text={
					isTask
						? getLabelMessage('taskCreatingPages:taskIsBeingCreated')
						: getLabelMessage('taskCreatingPages:leadIsBeingCreated')
				}
			/>
			<Grid size={12}>
				<Grid container justifyContent="space-around">
					<Grid size={3}>
						<ComponentLoader fetchStorePath={urls.task} ignoreErrors>
							<ComponentLoader fetchStorePath={urls.createTaskAttachment} ignoreErrors>
								<SubmitButton
									variant="contained"
									onClick={handleSubmit}
									onClickError={handleShowDialogsForAddSubmit}
									fullWidth
									text={
										isTask
											? getLabelMessage('taskCreatingPages:createTask')
											: getLabelMessage('taskCreatingPages:createLead')
									}
								/>
							</ComponentLoader>
						</ComponentLoader>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

ActionButtons.propTypes = {
	formName: PropTypes.object,
};

export default ActionButtons;
